import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { SignInButton, SignUpButton } from "@clerk/react";
import { X, ArrowRight, Lock } from "lucide-react";
import { IconClapperboard, IconStarBurst, IconSparkleWand } from "./cartoon-icons";
import { CTLogo } from "./ct-logo";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** When set, shows the user's first frame (blurred) as a locked teaser. */
  previewImageUrl?: string | null;
}

const FEATURES = [
  { Icon: IconClapperboard, text: "Text & image to video" },
  { Icon: IconStarBurst, text: "AI character creation" },
  { Icon: IconSparkleWand, text: "Image editing & remix" },
];

export function AuthModal({ isOpen, onClose, title, previewImageUrl }: AuthModalProps) {
  const redirectUrl = typeof window !== "undefined"
    ? window.location.pathname + window.location.search
    : "/";

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Gated-generation scenario: keep the focus on the user's own (blurred)
  // generation with a single Sign Up CTA. No feature list, no big secondary
  // button — just the teaser and the one action that unlocks it.
  if (previewImageUrl) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-[#241d3a]/35 backdrop-blur-md"
          onClick={onClose}
        />
        <div className="relative w-full max-w-[400px] rounded-3xl overflow-hidden border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] animate-modal-in z-10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-xl bg-[#241d3a]/30 text-white/60 hover:text-white hover:bg-[#241d3a]/50 transition-colors border border-white/10"
            aria-label="Close"
          >
            <X className="size-5" />
          </button>

          <div className="relative h-[min(560px,82vh)] w-full">
            <img
              src={previewImageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "blur(15px)", transform: "scale(1.25)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#241d3a]/20 via-[#241d3a]/35 to-[#241d3a]/90" />

            <div className="relative h-full flex flex-col items-center justify-end text-center px-7 pb-8">
              <div className="size-14 rounded-full bg-white/15 border border-white/25 flex items-center justify-center mb-4">
                <Lock className="size-6 text-white" />
              </div>
              <h2
                className="text-[26px] text-white tracking-tight leading-tight drop-shadow-lg"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}
              >
                {title || "Sign Up to View Generation"}
              </h2>
              <p className="text-[13px] text-white/75 font-semibold mt-2 mb-6 drop-shadow max-w-[280px]">
                Sign up to view &amp; download your generation in full quality.
              </p>

              <SignUpButton
                mode="modal"
                forceRedirectUrl={redirectUrl}
                signInForceRedirectUrl={redirectUrl}
              >
                <button
                  onClick={onClose}
                  className="w-full py-3.5 rounded-2xl bg-[#b8a5f0] border-2 border-[#2a2342] text-[15px] font-extrabold text-[#241d3a] shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:shadow-[5px_5px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:bg-[#c8b8f5] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all flex items-center justify-center gap-2"
                >
                  Sign Up Free <ArrowRight className="size-4" />
                </button>
              </SignUpButton>

              <SignInButton
                mode="modal"
                forceRedirectUrl={redirectUrl}
                signUpForceRedirectUrl={redirectUrl}
              >
                <button
                  onClick={onClose}
                  className="mt-3.5 text-[12.5px] text-white/65 hover:text-white font-bold transition-colors duration-200"
                >
                  Already have an account?{" "}
                  <span className="underline underline-offset-2">Log in</span>
                </button>
              </SignInButton>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const preview = previewImageUrl ? (
    <div
      className="relative rounded-2xl overflow-hidden border-2 border-[#2a2342] mb-6"
      style={{ aspectRatio: "16/10" }}
    >
      <img
        src={previewImageUrl}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "blur(16px)", transform: "scale(1.2)" }}
      />
      <div className="absolute inset-0 bg-[#241d3a]/45 flex flex-col items-center justify-center gap-2">
        <div className="size-12 rounded-full bg-white/15 border border-white/25 flex items-center justify-center">
          <Lock className="size-5 text-white" />
        </div>
        <p className="text-white text-[13px] font-extrabold drop-shadow">
          Your video is ready
        </p>
      </div>
    </div>
  ) : null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <div className="fixed inset-0 bg-[#241d3a]/35 backdrop-blur-md" onClick={onClose} />

      {/* Desktop */}
      <div className="hidden md:flex fixed inset-0 items-center justify-center z-10 p-6">
        <div className="w-full max-w-[440px] bg-white rounded-3xl border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] animate-modal-in overflow-hidden">
          <div className="relative px-8 pt-8 pb-8">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl bg-[#241d3a]/[0.06] text-[#241d3a]/55 hover:text-[#241d3a] hover:bg-[#241d3a]/[0.09] transition-colors">
              <X className="size-5" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <CTLogo className="size-10" />
              <span className="text-[18px] text-[#241d3a] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>CartoonTale</span>
            </div>

            {preview}

            <h2 className="text-3xl text-[#241d3a] tracking-tight leading-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>
              {title || <>CREATE VIDEOS<br />&amp; IMAGES WITH AI<span className="text-[#7c5cf0]">.</span></>}
            </h2>

            <div className="flex flex-col gap-3 mb-8">
              {FEATURES.map((f) => (
                <div key={f.text} className="flex items-center gap-3">
                  <f.Icon className="size-7" />
                  <span className="text-[14px] font-bold text-[#241d3a]/60">{f.text}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <SignUpButton mode="modal" forceRedirectUrl={redirectUrl} signInForceRedirectUrl={redirectUrl}>
                <button onClick={onClose} className="w-full py-3.5 rounded-2xl bg-[#b8a5f0] border-2 border-[#2a2342] text-[15px] font-extrabold text-[#241d3a] shadow-[3px_3px_0_rgba(42,35,66,0.18)] hover:shadow-[5px_5px_0_rgba(42,35,66,0.18)] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:bg-[#c8b8f5] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all flex items-center justify-center gap-2">
                  Sign Up Free <ArrowRight className="size-4" />
                </button>
              </SignUpButton>

              <SignInButton mode="modal" forceRedirectUrl={redirectUrl} signUpForceRedirectUrl={redirectUrl}>
                <button onClick={onClose} className="w-full py-3.5 rounded-2xl bg-[#241d3a]/[0.06] border-2 border-[#241d3a]/15 text-[15px] font-extrabold text-[#241d3a] hover:bg-[#241d3a]/[0.08] active:bg-[#241d3a]/[0.04] transition-all flex items-center justify-center gap-2">
                  Log In
                </button>
              </SignInButton>
            </div>

            <div className="mt-6 flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#241d3a]/[0.04] border border-[#241d3a]/10">
              <span className="size-8 rounded-xl bg-gradient-to-br from-[#b8a5f0] to-[#9d86e8] flex items-center justify-center text-[#241d3a] font-black text-sm shrink-0">T</span>
              <p className="text-[12px] text-[#241d3a]/55 font-semibold leading-snug">
                <strong className="text-[#241d3a] font-extrabold">42 free tokens</strong> on signup. No credit card required.
              </p>
            </div>

            <p className="text-[11px] text-[#241d3a]/35 font-semibold text-center mt-5">
              By signing up you agree to our{" "}
              <Link to="/terms" onClick={onClose} className="text-[#241d3a]/55 font-bold hover:underline">Terms</Link>
              {" "}and content policy.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile: 80% height sheet */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-10 animate-slide-up" style={{ height: "80dvh" }}>
        <div className="bg-white rounded-t-3xl border-t-2 border-x-2 border-[#2a2342] h-full flex flex-col">
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <div className="w-10 h-1 rounded-full bg-[#241d3a]/[0.09]" />
          </div>

          <div className="flex-1 flex flex-col justify-between px-6 overflow-y-auto" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}>
            <div>
              <div className="flex items-center gap-2.5 mb-5 mt-2">
                <CTLogo className="size-9" />
                <span className="text-[16px] text-[#241d3a] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>CartoonTale</span>
              </div>

              {preview}
              <h2 className="text-2xl text-[#241d3a] tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900 }}>{title || <>CREATE VIDEOS &amp; IMAGES<span className="text-[#7c5cf0]">.</span></>}</h2>
              <p className="text-[14px] text-[#241d3a]/60 font-semibold mb-6">AI-powered video and image generation.</p>

              <div className="flex flex-col gap-3 mb-6">
                {FEATURES.map((f) => (
                  <div key={f.text} className="flex items-center gap-3">
                    <f.Icon className="size-7" />
                    <span className="text-[14px] font-bold text-[#241d3a]/60">{f.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#241d3a]/[0.04] border border-[#241d3a]/10 mb-6">
                <span className="size-8 rounded-xl bg-gradient-to-br from-[#b8a5f0] to-[#9d86e8] flex items-center justify-center text-[#241d3a] font-black text-sm shrink-0">T</span>
                <p className="text-[12px] text-[#241d3a]/55 font-semibold leading-snug">
                  <strong className="text-[#241d3a] font-extrabold">42 free tokens</strong> on signup. No credit card required.
                </p>
              </div>
            </div>

            <div className="shrink-0">
              <div className="space-y-2.5">
                <SignUpButton mode="modal" forceRedirectUrl={redirectUrl} signInForceRedirectUrl={redirectUrl}>
                  <button onClick={onClose} className="w-full py-3.5 rounded-2xl bg-[#b8a5f0] border-2 border-[#2a2342] text-[15px] font-extrabold text-[#241d3a] shadow-[2px_2px_0_rgba(42,35,66,0.16)] hover:bg-[#c8b8f5] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_rgba(42,35,66,0.16)] transition-all flex items-center justify-center gap-2">
                    Sign Up Free <ArrowRight className="size-4" />
                  </button>
                </SignUpButton>

                <SignInButton mode="modal" forceRedirectUrl={redirectUrl} signUpForceRedirectUrl={redirectUrl}>
                  <button onClick={onClose} className="w-full py-3 rounded-2xl bg-[#241d3a]/[0.06] border-2 border-[#241d3a]/15 text-[14px] font-extrabold text-[#241d3a] hover:bg-[#241d3a]/[0.08] transition-all flex items-center justify-center gap-2">
                    Log In
                  </button>
                </SignInButton>
              </div>

              <p className="text-[10px] text-[#241d3a]/35 font-semibold mt-4 text-center pb-1">
                42 free tokens · No credit card ·{" "}
                <Link to="/terms" onClick={onClose} className="text-[#241d3a]/55 font-bold hover:underline">Terms</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
